import React, { useState } from 'react';
import { Edit, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const BulkOperationsPanel = ({ leads, advisorId, onComplete }) => {
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [operation, setOperation] = useState('');
  const [operationValue, setOperationValue] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedLeads(leads?.map(l => l?.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    if (selectedLeads?.includes(leadId)) {
      setSelectedLeads(selectedLeads?.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const handleBulkOperation = async () => {
    if (selectedLeads?.length === 0) {
      setError('Please select at least one lead');
      return;
    }

    if (!operation) {
      setError('Please select an operation');
      return;
    }

    try {
      setProcessing(true);
      setError('');

      let updateData = {};

      switch (operation) {
        case 'change_stage':
          updateData = { stage: operationValue };
          break;
        case 'change_interest':
          updateData = { nivel_interes: operationValue };
          break;
        case 'change_source':
          updateData = { fuente: operationValue };
          break;
        case 'reassign':
          updateData = { advisor_id: operationValue };
          break;
        case 'delete':
          const { error: deleteError } = await supabase
            ?.from('crm_leads')
            ?.delete()
            ?.in('id', selectedLeads);
          
          if (deleteError) throw deleteError;
          
          setSelectedLeads([]);
          if (onComplete) onComplete();
          return;
        default:
          throw new Error('Invalid operation');
      }

      const { error: updateError } = await supabase
        ?.from('crm_leads')
        ?.update(updateData)
        ?.in('id', selectedLeads);

      if (updateError) throw updateError;

      setSelectedLeads([]);
      setOperation('');
      setOperationValue('');
      
      if (onComplete) onComplete();
    } catch (err) {
      setError(err?.message || 'Operation failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Bulk Operations</h2>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {/* Operation Controls */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Select Operation</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Operation Type</label>
            <select
              value={operation}
              onChange={(e) => {
                setOperation(e?.target?.value);
                setOperationValue('');
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Select Operation --</option>
              <option value="change_stage">Change Stage</option>
              <option value="change_interest">Change Interest Level</option>
              <option value="change_source">Change Source</option>
              <option value="delete">Delete Leads</option>
            </select>
          </div>

          {operation && operation !== 'delete' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Value</label>
              {operation === 'change_stage' && (
                <select
                  value={operationValue}
                  onChange={(e) => setOperationValue(e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Select Stage --</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal</option>
                  <option value="negotiation">Negotiation</option>
                  <option value="closed_won">Closed Won</option>
                  <option value="closed_lost">Closed Lost</option>
                </select>
              )}
              {operation === 'change_interest' && (
                <select
                  value={operationValue}
                  onChange={(e) => setOperationValue(e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Select Interest Level --</option>
                  <option value="cold">Cold</option>
                  <option value="warm">Warm</option>
                  <option value="hot">Hot</option>
                </select>
              )}
              {operation === 'change_source' && (
                <select
                  value={operationValue}
                  onChange={(e) => setOperationValue(e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Select Source --</option>
                  <option value="referral">Referral</option>
                  <option value="website">Website</option>
                  <option value="social_media">Social Media</option>
                  <option value="direct">Direct</option>
                  <option value="event">Event</option>
                  <option value="other">Other</option>
                </select>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleBulkOperation}
            disabled={processing || selectedLeads?.length === 0 || !operation || (operation !== 'delete' && !operationValue)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <Edit className="w-4 h-4" />
                Apply to {selectedLeads?.length} Lead{selectedLeads?.length !== 1 ? 's' : ''}
              </>
            )}
          </button>

          <button
            onClick={() => setSelectedLeads([])}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear Selection
          </button>
        </div>
      </div>
      {/* Lead Selection Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4">
                <input
                  type="checkbox"
                  checked={selectedLeads?.length === leads?.length && leads?.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Stage</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Interest</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Source</th>
            </tr>
          </thead>
          <tbody>
            {leads?.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-gray-500">
                  No leads available
                </td>
              </tr>
            ) : (
              leads?.map((lead) => (
                <tr key={lead?.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={selectedLeads?.includes(lead?.id)}
                      onChange={() => handleSelectLead(lead?.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                  <td className="py-3 px-4">{lead?.nombre}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{lead?.email}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                      {lead?.stage}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      lead?.nivelInteres === 'hot' ? 'bg-red-100 text-red-800' :
                      lead?.nivelInteres === 'warm'? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {lead?.nivelInteres}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {lead?.fuente}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BulkOperationsPanel;